import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Eye } from "lucide-react";
import { Button, Badge } from "@repo/ui";
import { ROLE_DISPLAY_NAMES } from "@repo/permissions";
import { DataTableColumnHeader } from "@repo/ui";
import type { AuditLog, AuditActionType } from "@/lib/types/inventory";

export const actionTypeLabels: Record<AuditActionType, string> = {
  receipt_created: "Receipt Created",
  receipt_edited: "Receipt Edited",
  receipt_deleted: "Receipt Deleted",
  dispense_created: "Dispense Created",
  dispense_edited: "Dispense Edited",
  dispense_deleted: "Dispense Deleted",
  inventory_created: "Inventory Created",
  inventory_updated: "Inventory Updated",
  inventory_deleted: "Inventory Deleted",
  stock_manual_override: "Stock Override",
  stock_override_cleared: "Override Cleared",
  stock_adjustment_created: "Adjustment Created",
  stock_adjustment_edited: "Adjustment Edited",
  stock_adjustment_deleted: "Adjustment Deleted",
  threshold_changed: "Threshold Changed",
  expiration_alert_changed: "Expiration Alert Changed",
  initial_stock_adjusted: "Initial Stock Adjusted",
  password_failed_attempt: "Password Failed",
  user_login: "User Login",
  user_logout: "User Logout",
  user_created: "User Created",
  user_deleted: "User Deleted",
  user_profile_updated: "Profile Updated",
  pharmacy_settings_changed: "Settings Changed",
  inventory_audit_created: "Audit Created",
  inventory_audit_deleted: "Audit Deleted",
  authorization_approved: "Authorization Approved",
  authorization_denied: "Authorization Denied",
  permission_override_changed: "Permission Override Changed",
  two_factor_enabled: "2FA Enabled",
  two_factor_disabled: "2FA Disabled",
  two_factor_verified: "2FA Verified",
  two_factor_recovery_used: "2FA Recovery Used",
  two_factor_recovery_regenerated: "2FA Recovery Regenerated",
  two_factor_enforcement_changed: "2FA Enforcement Changed",
};

const actionTypeColors: Record<AuditActionType, string> = {
  receipt_created: "bg-surface font-semibold text-emerald-600 border-border",
  receipt_edited: "bg-surface font-semibold text-primary border-border",
  receipt_deleted: "bg-surface font-semibold text-destructive border-border",
  dispense_created: "bg-surface font-semibold text-emerald-600 border-border",
  dispense_edited: "bg-surface font-semibold text-primary border-border",
  dispense_deleted: "bg-surface font-semibold text-destructive border-border",
  inventory_created: "bg-surface font-semibold text-emerald-600 border-border",
  inventory_updated: "bg-surface font-semibold text-primary border-border",
  inventory_deleted: "bg-surface font-semibold text-destructive border-border",
  stock_manual_override:
    "bg-surface font-semibold text-amber-600 border-border",
  stock_override_cleared:
    "bg-surface font-semibold text-amber-600 border-border",
  stock_adjustment_created:
    "bg-surface font-semibold text-emerald-600 border-border",
  stock_adjustment_edited:
    "bg-surface font-semibold text-primary border-border",
  stock_adjustment_deleted:
    "bg-surface font-semibold text-destructive border-border",
  threshold_changed: "bg-surface font-semibold text-primary border-border",
  expiration_alert_changed:
    "bg-surface font-semibold text-primary border-border",
  initial_stock_adjusted:
    "bg-surface font-semibold text-amber-600 border-border",
  password_failed_attempt:
    "bg-surface font-semibold text-destructive border-border",
  user_login: "bg-surface font-semibold text-muted-foreground border-border",
  user_logout: "bg-surface font-semibold text-muted-foreground border-border",
  user_created: "bg-surface font-semibold text-emerald-600 border-border",
  user_deleted: "bg-surface font-semibold text-destructive border-border",
  user_profile_updated: "bg-surface font-semibold text-primary border-border",
  pharmacy_settings_changed:
    "bg-surface font-semibold text-primary border-border",
  inventory_audit_created: "bg-surface font-semibold text-accent border-border",
  inventory_audit_deleted:
    "bg-surface font-semibold text-destructive border-border",
  authorization_approved:
    "bg-surface font-semibold text-emerald-600 border-border",
  authorization_denied:
    "bg-surface font-semibold text-destructive border-border",
  permission_override_changed:
    "bg-surface font-semibold text-amber-600 border-border",
  two_factor_enabled: "bg-surface font-semibold text-emerald-600 border-border",
  two_factor_disabled:
    "bg-surface font-semibold text-destructive border-border",
  two_factor_verified: "bg-surface font-semibold text-primary border-border",
  two_factor_recovery_used:
    "bg-surface font-semibold text-amber-600 border-border",
  two_factor_recovery_regenerated:
    "bg-surface font-semibold text-amber-600 border-border",
  two_factor_enforcement_changed:
    "bg-surface font-semibold text-primary border-border",
};

export function ActionTypeBadge({
  actionType,
}: {
  actionType: AuditActionType;
}) {
  return (
    <Badge variant="outline" className={actionTypeColors[actionType]}>
      {actionTypeLabels[actionType]}
    </Badge>
  );
}

export function createAuditLogColumns(
  onView: (log: AuditLog) => void
): ColumnDef<AuditLog>[] {
  return [
    {
      accessorKey: "action_timestamp",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Timestamp" />
      ),
      cell: ({ row }) => {
        const date = row.getValue("action_timestamp") as string;
        if (!date) return <span className="text-muted-foreground">—</span>;
        const parsed = new Date(date);
        if (isNaN(parsed.getTime()))
          return <span className="text-muted-foreground">Invalid date</span>;
        return (
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              {format(parsed, "MMM dd, yyyy")}
            </span>
            <span className="text-xs text-muted-foreground">
              {format(parsed, "HH:mm:ss")}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "user_name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="User" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              {row.getValue("user_name")}
            </span>
            <span className="text-xs text-muted-foreground">
              {ROLE_DISPLAY_NAMES[
                row.original.user_role as keyof typeof ROLE_DISPLAY_NAMES
              ] || row.original.user_role}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "action_type",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Action" />
      ),
      cell: ({ row }) => {
        const actionType = row.getValue("action_type") as AuditActionType;
        return <ActionTypeBadge actionType={actionType} />;
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "table_name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Record Type" />
      ),
      cell: ({ row }) => {
        const tableName = row.getValue("table_name") as string;
        const friendlyNames: Record<string, string> = {
          receipts: "Receipt",
          dispenses: "Dispense",
          inventory: "Inventory",
          users: "User",
          pharmacies: "Settings",
          stock_adjustments: "Adjustment",
          inventory_audits: "Inventory Audit",
          audit_logs: "Audit Log",
          authorization_approvals: "Authorization",
        };
        return (
          <Badge
            variant="outline"
            className="bg-surface font-semibold text-muted-foreground border-border"
          >
            {friendlyNames[tableName] || tableName}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <Button variant="ghost" size="sm" onClick={() => onView(row.original)}>
          <Eye className="h-4 w-4 mr-2" />
          View
        </Button>
      ),
    },
  ];
}
